import { FC, Fragment, useMemo } from "react";

import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

import { classNames } from "@/shared/utils/classnames";
import { Option } from "@/shared/utils/options";

import { SpanIconCheckSelect } from "./SpanIconCheckSelect";

export type SelectProps = {
  className?: string;
  hidden?: boolean;
  optional?: boolean;
  options: Array<Option>;
  value: any;
  onChange: (value: any) => void;
  name: string;
  label?: string;
  disabled?: boolean;
  error?: string;
};

export const Select: FC<SelectProps> = ({
  className,
  hidden,
  name,
  value,
  options,
  onChange,
  optional,
  error,
  label,
  disabled = false,
}) => {
  
  const _options = useMemo(() => {
    const safeOptions = Array.isArray(options) ? options : [];
    return optional
      ? [{ element: "Nada seleccionado", value: undefined }, ...safeOptions]
      : safeOptions;
  }, [options, optional]);

  const selected = useMemo(() => {
    return _options.find((option) => option.value == value);
  }, [value, _options]);

  return (
    <div className={classNames(hidden ? "hidden" : "", className)}>
      <Listbox
        value={undefined}
        disabled={disabled}
        onChange={(value) =>
          onChange({
            target: {
              name,
              value,
            },
          })
        }
        name={name}
      >
        <div>
          <div>
            <Label className="block text-sm font-medium leading-6 text-gray-600">
              {label}
            </Label>
          </div>
          <div className="relative">
            <ListboxButton
              className={classNames(
                "relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-400 shadow-sm ring-1 ring-inset sm:text-sm sm:leading-6",
                error
                  ? "ring-red-300 placeholder:text-red-300"
                  : "ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              )}
            >
              <span className="block truncate">
                {selected ? selected.element : "Nada seleccionado"}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-400"
                />
              </span>
            </ListboxButton>
            <Fragment>
              <ListboxOptions
                transition
                className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
              >
                {_options &&
                  _options?.map((option, index) => (
                    <ListboxOption
                      key={index}
                      value={option.value}
                      className="group relative cursor-default select-none py-2 pl-8 pr-4 text-gray-300 data-[focus]:bg-primary data-[focus]:text-white"
                    >
                      <span className="block truncate font-normal group-data-[selected]:font-semibold text-gray-900">
                        {option.element}
                      </span>
                      <SpanIconCheckSelect />
                    </ListboxOption>
                  ))}
              </ListboxOptions>
            </Fragment>
          </div>
        </div>
      </Listbox>
    </div>
  );
};
